import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  untracked,
} from '@angular/core';
import type { FormValueControl } from '@angular/forms/signals';
import { InputEventType } from '../../constants/input-event-types.constants';
import {
  caretAfterDigits,
  conformToMask,
  digitsOf,
  isDigit,
  isMaskLiteral,
  skipLiteralsBackward,
  skipLiteralsForward,
  trailingDigits,
} from './input-mask';

/**
 * Masks a native text input (`<input [formField]="f.phone" uiInputMask="(000) 000-0000">`): the
 * field shows the masked text and its value is the digits alone. Edits that only step over a
 * literal are handled before the browser applies them; everything else — typing, deleting,
 * undo, autofill — lands natively and is then fitted back into the mask.
 */
@Directive({
  selector: 'input[uiInputMask]',
  host: {
    '(beforeinput)': 'onBeforeInput($event)',
    '(input)': 'onInput($event)',
    '(paste)': 'onPaste($event)',
    '(blur)': 'touch.emit()',
  },
})
export class UiInputMaskDirective implements FormValueControl<string> {
  private readonly element: HTMLInputElement = inject(ElementRef).nativeElement;

  readonly value = model('');
  readonly mask = input.required<string>({ alias: 'uiInputMask' });
  /**
   * A paste or an autofill replaces the whole value, and one with more digits than the mask keeps
   * its last ones — a phone pasted with its country code keeps the number itself. Without it a
   * paste lands at the caret like typing and the overflow is cut from the end.
   */
  readonly keepTrailingDigits = input(false);

  readonly touch = output<void>();

  constructor() {
    // A bound value is only shown through the mask, never written back: a field rendered under a
    // fallback mask before the real one arrives would otherwise cut the stored value down to it.
    effect(() => {
      const mask = this.mask();
      const text = conformToMask(this.value() ?? '', mask);

      untracked(() => {
        // Literals the user typed after the last digit are not part of the value: only a
        // different value or mask rewrites the text.
        const current = this.element.value;

        if (digitsOf(current) !== digitsOf(text) || conformToMask(current, mask) !== current) {
          this.element.value = text;
        }
      });
    });
  }

  protected onBeforeInput(event: InputEvent): void {
    const text = this.element.value;
    const start = this.element.selectionStart ?? text.length;
    const end = this.element.selectionEnd ?? start;
    const mask = this.mask();

    if (
      event.inputType === InputEventType.InsertText &&
      event.data?.length === 1 &&
      !isDigit(event.data)
    ) {
      if (!isMaskLiteral(event.data, mask)) {
        event.preventDefault();
      } else if (start === end && start < text.length) {
        // Typing the literal that is already there steps over it.
        event.preventDefault();
        this.moveCaret(text[start] === event.data ? start + 1 : start);
      }
      return;
    }

    if (start !== end) return;

    if (
      event.inputType === InputEventType.DeleteContentBackward &&
      start > 0 &&
      start < text.length &&
      !isDigit(text[start - 1]!)
    ) {
      event.preventDefault();
      this.moveCaret(skipLiteralsBackward(text, start));
    } else if (
      event.inputType === InputEventType.DeleteContentForward &&
      start < text.length &&
      !isDigit(text[start]!)
    ) {
      event.preventDefault();
      this.moveCaret(start === 0 ? 0 : skipLiteralsForward(text, start));
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    if (!this.keepTrailingDigits() || this.element.readOnly) return;

    event.preventDefault();
    this.replaceWith(event.clipboardData?.getData('text') ?? '');
  }

  protected onInput(event: Event): void {
    const inputType = (event as InputEvent).inputType ?? '';

    // Autofill replaces the whole value and fires a plain `input` event.
    if (
      this.keepTrailingDigits() &&
      (!(event instanceof InputEvent) || inputType === InputEventType.InsertReplacementText)
    ) {
      this.replaceWith(this.element.value);
      return;
    }

    const raw = this.element.value;
    const caret = this.element.selectionStart ?? raw.length;
    const deleting = inputType.startsWith('delete');
    let text = conformToMask(raw, this.mask());

    if (deleting && !digitsOf(text)) {
      text = '';
    }

    let position = caretAfterDigits(text, digitsOf(raw.slice(0, caret)).length);

    if (inputType === InputEventType.DeleteContentForward && caret < raw.length) {
      position = caret;
    } else if (caret >= raw.length) {
      position = text.length;
    }

    if (this.element.value !== text) {
      this.element.value = text;
    }

    this.moveCaret(Math.min(position, text.length));
    this.value.set(digitsOf(text));
  }

  private replaceWith(raw: string): void {
    const mask = this.mask();
    const text = conformToMask(trailingDigits(raw, mask), mask);

    this.element.value = text;
    this.moveCaret(text.length);
    this.value.set(digitsOf(text));
  }

  private moveCaret(position: number): void {
    if (this.element.ownerDocument.activeElement === this.element) {
      this.element.setSelectionRange(position, position);
    }
  }
}
